import { useEffect, useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { fetch } from "@/src/lib/fetch";
import { cn } from "@/src/lib/utils";

/** Notes & timeline (brief §16): status history assembled from real timestamps. */
type Ev = { at: string; kind: string; title: string; detail: string | null; by: string | null };
const KIND: Record<string, string> = {
  booking: "bg-slate-100 text-slate-700",
  offer: "bg-indigo-50 text-indigo-700",
  status: "bg-amber-100 text-amber-800",
  payment: "bg-green-100 text-green-800",
  contract: "bg-blue-100 text-blue-800",
  comms: "bg-purple-50 text-purple-700",
  review: "bg-pink-50 text-pink-700",
};
const when = (s: string) => new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export function TimelineSection({ bookingId }: { bookingId: string }) {
  const [data, setData] = useState<{ currentStatus: string | null; events: Ev[] } | null>(null);
  useEffect(() => {
    fetch<{ data: any }>(`/api/bookings/${bookingId}/timeline`).then((r: any) => setData(r.data)).catch(() => setData(null));
  }, [bookingId]);
  if (!data) return null;
  return (
    <Card className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Timeline</h3>
        <p className="text-xs text-slate-500">
          {data.events.length} events{data.currentStatus ? ` · now ${String(data.currentStatus).replace(/_/g, " ")}` : ""}
        </p>
      </div>
      {data.events.length === 0 && <p className="text-sm text-slate-500">Nothing recorded yet.</p>}
      <ol className="relative border-l border-slate-200 pl-4">
        {data.events.map((e, i) => (
          <li key={i} className="mb-3">
            <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full border border-white bg-slate-300" />
            <div className="flex flex-wrap items-baseline gap-2">
              <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", KIND[e.kind] ?? "bg-slate-100 text-slate-700")}>{e.kind}</span>
              <span className="text-sm font-medium">{e.title}</span>
              <span className="text-xs text-slate-400">{when(e.at)}{e.by ? ` · ${e.by}` : ""}</span>
            </div>
            {e.detail && <p className="text-xs text-slate-500">{e.detail}</p>}
          </li>
        ))}
      </ol>
      <p className="text-[11px] text-slate-400">Status changes are recorded from 17 Sep 2026; earlier changes weren't logged.</p>
    </Card>
  );
}
